import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Button,
  Input,
  Textarea,
  Select,
  SelectItem,
} from '@heroui/react';
import React, { useState } from 'react';
import { CreateCardRequest, OrderBoardColumn } from '../../types/orderBoard';

interface CreateCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (request: CreateCardRequest) => Promise<void>;
  boardId: number;
  columns: OrderBoardColumn[];
}

const CreateCardModal: React.FC<CreateCardModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  boardId,
  columns,
}) => {
  const [columnId, setColumnId] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!title.trim() || !columnId) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm({
        boardId,
        columnId,
        title: title.trim(),
        description: description.trim() || undefined,
      });
      handleClose();
    } catch (error) {
      // Error handling is done by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setColumnId(null);
    setTitle('');
    setDescription('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size='md'
      scrollBehavior='inside'
      classNames={{
        base: 'max-h-[90vh]',
        body: 'overflow-y-auto',
      }}
    >
      <ModalContent>
        <ModalHeader>Create Card</ModalHeader>
        <ModalBody>
          <div className='space-y-4'>
            <Select
              label='Column'
              placeholder='Select a column'
              selectedKeys={columnId ? [columnId.toString()] : []}
              onSelectionChange={keys => {
                const selected = Array.from(keys)[0] as string;
                setColumnId(selected ? parseInt(selected) : null);
              }}
              isRequired
            >
              {columns.map(column => (
                <SelectItem key={column.id.toString()}>
                  {column.name}
                </SelectItem>
              ))}
            </Select>

            <Input
              label='Card Title'
              placeholder='Enter card title'
              value={title}
              onChange={e => setTitle(e.target.value)}
              isRequired
              autoFocus
            />

            <Textarea
              label='Description'
              placeholder='Optional description for this card'
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant='ghost' onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color='primary'
            onClick={handleConfirm}
            disabled={!title.trim() || !columnId || isSubmitting}
            isLoading={isSubmitting}
          >
            Create Card
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateCardModal;
